/**
 * src/api/client.js
 * Axios instance configured for the Process Tracker backend.
 * Automatically attaches JWT token from localStorage to every request.
 */

import axios from 'axios';
import Sentry from '../lib/sentry';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ||
  'https://process-tracker-backend-production.up.railway.app';

const api = axios.create({
  baseURL: BACKEND_URL,
  headers: { 'Content-Type': 'application/json' }
});

// Attach JWT token to every request
api.interceptors.request.use(config => {
  const token = localStorage.getItem('pt_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      localStorage.removeItem('pt_token');
      localStorage.removeItem('pt_user');
      window.location.href = '/login';
    }
    // Redirect unverified users to the verification page
    if (error.response?.status === 403 &&
        error.response?.data?.error === 'email_verification_required') {
      window.location.href = '/verify-email-pending';
    }
    // Capture 5xx errors in Sentry (skip 4xx — expected application flows)
    if (error.response?.status >= 500) {
      Sentry.withScope(scope => {
        scope.setTag('api_url', error.config?.url);
        scope.setTag('api_method', error.config?.method);
        scope.setTag('status', error.response.status);
        scope.setExtra('response_data', error.response.data);
        Sentry.captureException(error);
      });
    }
    return Promise.reject(error);
  }
);

export default api;
